import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Play, Plus, Edit3, Trash2, X, ChevronLeft, ChevronRight, Wand2, Presentation as PresentIcon, Loader2, Maximize2 } from 'lucide-react';
import './Presentations.css';
import JacksonStopsCard from './presentation/AssetChecklist';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Count-up ──────────────────────────────────────────────────────
function useCountUp(target, duration = 1400, active = true) {
  const [val, setVal] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    if (!active || target === undefined || target === null || isNaN(target)) return;
    const start = performance.now();
    const step = (ts) => {
      const p = Math.min((ts - start) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setVal(target * ease);
      if (p < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration, active]);
  return val;
}

// Parse a money string like "£675,000" or "£1.17M" into a number + original formatting
function parseMoney(str) {
  if (!str) return null;
  const s = str.replace(/\s/g, '');
  const m = s.match(/£([\d,.]+)([KMBkmb]?)/);
  if (!m) return null;
  const base = parseFloat(m[1].replace(/,/g, ''));
  if (isNaN(base)) return null;
  const mult = { K: 1e3, M: 1e6, B: 1e9 }[m[2].toUpperCase()] || 1;
  return { value: base * mult, raw: m[0], hasSuffix: !!m[2] };
}

function formatMoney(num, hasSuffix) {
  if (hasSuffix) {
    if (num >= 1e9) return `£${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `£${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `£${(num / 1e3).toFixed(0)}K`;
  }
  return `£${Math.round(num).toLocaleString('en-GB')}`;
}

// ── Animated money value ──────────────────────────────────────────
function MoneyTicker({ raw, delay = 0, active }) {
  const parsed = parseMoney(raw);
  const [start, setStart] = useState(false);
  useEffect(() => {
    if (!active) { setStart(false); return; }
    const t = setTimeout(() => setStart(true), delay);
    return () => clearTimeout(t);
  }, [active, delay]);
  const value = useCountUp(parsed ? parsed.value : 0, 1600, start);
  if (!parsed) return <span className="pz-money">{raw}</span>;
  return <span className="pz-money">{formatMoney(value, parsed.hasSuffix)}</span>;
}

// Render a line with money values highlighted
function LineRenderer({ text, money, active, delayBase }) {
  if (!money || money.length === 0) return <>{text}</>;
  // Split text by first money match, rendering MoneyTicker in between
  let rest = text;
  const parts = [];
  money.forEach((m, i) => {
    const idx = rest.indexOf(m);
    if (idx === -1) return;
    if (idx > 0) parts.push(<span key={`t${i}`}>{rest.slice(0, idx)}</span>);
    parts.push(<MoneyTicker key={`m${i}`} raw={m} delay={delayBase + i * 150} active={active} />);
    rest = rest.slice(idx + m.length);
  });
  if (rest) parts.push(<span key="tail">{rest}</span>);
  return <>{parts}</>;
}

// ── Slide Renderer ────────────────────────────────────────────────
function SlideView({ slide, index, total, active }) {
  // Stagger bullets
  return (
    <div className={`pz-slide ${active ? 'pz-slide-active' : ''}`} data-testid={`pz-slide-${index}`}>
      <div className="pz-slide-title-wrap" style={{ animationDelay: '0ms' }}>
        <h1 className="pz-slide-title">{slide.title}</h1>
      </div>
      <div className="pz-slide-body">
        {(slide.lines || []).map((line, i) => {
          const delay = 400 + i * 150;
          return (
            <div
              key={i}
              className={`pz-line pz-line-${line.kind}`}
              style={{ animationDelay: `${delay}ms` }}
              data-testid={`pz-line-${index}-${i}`}
            >
              <LineRenderer text={line.text} money={line.money} active={active} delayBase={delay} />
            </div>
          );
        })}
      </div>
      <div className="pz-slide-counter" data-testid={`pz-counter-${index}`}>
        {index + 1} <span>/</span> {total}
      </div>
    </div>
  );
}

// ── Fullscreen Player ─────────────────────────────────────────────
function PresentationPlayer({ presentation, onClose }) {
  const [started, setStarted] = useState(false);
  const [idx, setIdx] = useState(0);
  const slides = presentation.slides || [];
  const total = slides.length;
  const containerRef = useRef(null);

  const next = useCallback(() => setIdx((i) => Math.min(i + 1, total - 1)), [total]);
  const prev = useCallback(() => setIdx((i) => Math.max(i - 1, 0)), []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (!started) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setStarted(true); }
        return;
      }
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); next(); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [started, next, prev, onClose]);

  const requestFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (el.requestFullscreen) el.requestFullscreen();
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
  };

  return (
    <div className="pz-player" ref={containerRef} data-testid="pz-player">
      <button className="pz-player-close" onClick={onClose} data-testid="pz-close-btn" title="Close (Esc)">
        <X size={22} />
      </button>
      <button className="pz-player-fs" onClick={requestFullscreen} title="Enter fullscreen">
        <Maximize2 size={18} />
      </button>

      {!started ? (
        <div className="pz-start" data-testid="pz-start-screen">
          <div className="pz-logo-wrap">
            <div className="pz-logo-ring" />
            <div className="pz-logo-ring pz-logo-ring-2" />
            <div className="pz-logo">
              <span className="pz-logo-niche">niche</span>
              <span className="pz-logo-com">com</span>
            </div>
          </div>
          <div className="pz-start-meta">
            <div className="pz-start-label">Board Presentation</div>
            <h2 className="pz-start-title">{presentation.title}</h2>
            <div className="pz-start-sub">{total} slide{total !== 1 ? 's' : ''}</div>
          </div>
          <button
            className="pz-play-btn"
            onClick={() => setStarted(true)}
            data-testid="pz-play-btn"
          >
            <Play size={28} fill="#0b0b0b" />
          </button>
          <div className="pz-kbd-hint">Press <kbd>Space</kbd> or click to begin · <kbd>→</kbd> next · <kbd>Esc</kbd> exit</div>
        </div>
      ) : (
        <>
          <div className="pz-stage">
            {slides.map((s, i) => (
              i === idx ? (
                <SlideView key={i} slide={s} index={i} total={total} active={true} />
              ) : null
            ))}
          </div>

          <button
            className="pz-nav pz-nav-prev"
            onClick={prev}
            disabled={idx === 0}
            data-testid="pz-prev-btn"
            aria-label="Previous slide"
          >
            <ChevronLeft size={28} />
          </button>
          <button
            className="pz-nav pz-nav-next"
            onClick={next}
            disabled={idx >= total - 1}
            data-testid="pz-next-btn"
            aria-label="Next slide"
          >
            <ChevronRight size={28} />
          </button>

          <div className="pz-progress">
            {slides.map((_, i) => (
              <span key={i} className={`pz-dot ${i === idx ? 'pz-dot-active' : ''} ${i < idx ? 'pz-dot-done' : ''}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Builder Modal ─────────────────────────────────────────────────
function BuilderModal({ existing, onClose, onSaved }) {
  const [title, setTitle] = useState(existing?.title || '');
  const [text, setText] = useState(existing?.source_text || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!text.trim()) { setError('Paste some content first'); return; }
    setSaving(true); setError('');
    try {
      const url = existing ? `${API}/api/admin/presentations/${existing.id}` : `${API}/api/admin/presentations`;
      const method = existing ? 'put' : 'post';
      const res = await axios[method](url, {
        title: title.trim() || 'Untitled Presentation',
        source_text: text,
      });
      onSaved(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const slideCount = (text.trim().match(/\n\s*\n+/g) || []).length + (text.trim() ? 1 : 0);

  return (
    <div className="pz-modal-backdrop" onClick={onClose}>
      <div className="pz-modal" onClick={(e) => e.stopPropagation()} data-testid="pz-builder-modal">
        <div className="pz-modal-head">
          <div>
            <div className="pz-modal-eyebrow">{existing ? 'Edit Presentation' : 'New Presentation'}</div>
            <h2>{existing ? 'Refine your deck' : 'Build a new deck'}</h2>
          </div>
          <button className="pz-icon-btn" onClick={onClose} data-testid="pz-modal-close">
            <X size={18} />
          </button>
        </div>
        <div className="pz-modal-body">
          <label className="pz-field">
            <span>Title</span>
            <input
              className="pz-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Phase 1 – Video Adoption"
              data-testid="pz-title-input"
            />
          </label>
          <label className="pz-field">
            <span>Content  <em>· separate each slide with a blank line</em></span>
            <textarea
              className="pz-textarea"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={`Paste your content here.\n\nEach slide is separated by a blank line.\n\nMoney values like £675,000 animate as counters.\n\nEmoji headings and 👉 callouts are supported.`}
              rows={16}
              data-testid="pz-text-input"
            />
          </label>
          {error && <div className="pz-error" data-testid="pz-error">{error}</div>}
        </div>
        <div className="pz-modal-foot">
          <span className="pz-slide-preview">Preview: <strong>{slideCount}</strong> slide{slideCount !== 1 ? 's' : ''}</span>
          <div className="pz-modal-actions">
            <button className="pz-btn pz-btn-ghost" onClick={onClose}>Cancel</button>
            <button
              className="pz-btn pz-btn-primary"
              onClick={submit}
              disabled={saving}
              data-testid="pz-build-btn"
            >
              {saving ? <Loader2 size={16} className="pz-spin" /> : <Wand2 size={16} />}
              {existing ? 'Rebuild' : 'Build'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────
export default function Presentations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [playing, setPlaying] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/presentations`);
      setItems(res.data.presentations || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSaved = (saved) => {
    setBuilderOpen(false);
    setEditing(null);
    load();
    // If we were editing the currently-playing one, refresh it
    if (playing && playing.id === saved.id) setPlaying(saved);
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/admin/presentations/${item.id}`);
      load();
    } catch { /* noop */ }
  };

  return (
    <div className="pz-wrap" data-testid="pz-wrap">
      <div className="pz-header">
        <div>
          <div className="pz-eyebrow">Board Room</div>
          <h1 className="pz-h1">Presentations</h1>
          <p className="pz-sub">Stats-driven decks you can present full-screen to the board.</p>
        </div>
        <button
          className="pz-btn pz-btn-primary pz-btn-lg"
          onClick={() => { setEditing(null); setBuilderOpen(true); }}
          data-testid="pz-new-btn"
        >
          <Plus size={18} /> New Presentation
        </button>
      </div>

      <JacksonStopsCard />

      {loading ? (
        <div className="pz-loading"><Loader2 className="pz-spin" size={22} /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="pz-empty" data-testid="pz-empty">
          <PresentIcon size={56} strokeWidth={1.25} />
          <h3>No presentations yet</h3>
          <p>Paste your stats, sections, or talking points and hit <strong>Build</strong> — each blank line becomes a slide.</p>
          <button className="pz-btn pz-btn-primary" onClick={() => { setEditing(null); setBuilderOpen(true); }}>
            <Plus size={16} /> Create your first
          </button>
        </div>
      ) : (
        <div className="pz-grid" data-testid="pz-grid">
          {items.map((p) => (
            <div key={p.id} className="pz-card" data-testid={`pz-card-${p.id}`}>
              <div className="pz-card-glow" />
              <div className="pz-card-head">
                <div className="pz-card-slides">{p.slide_count} slide{p.slide_count !== 1 ? 's' : ''}</div>
                <div className="pz-card-actions">
                  <button className="pz-icon-btn" title="Edit" onClick={() => { setEditing(p); setBuilderOpen(true); }} data-testid={`pz-edit-${p.id}`}>
                    <Edit3 size={15} />
                  </button>
                  <button className="pz-icon-btn" title="Delete" onClick={() => remove(p)} data-testid={`pz-delete-${p.id}`}>
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
              <h3 className="pz-card-title">{p.title}</h3>
              <div className="pz-card-preview">
                {(p.slides || []).slice(0, 3).map((s, i) => (
                  <div key={i} className="pz-card-preview-row">
                    <span className="pz-card-preview-idx">{i + 1}</span>
                    <span className="pz-card-preview-text">{s.title_clean || s.title}</span>
                  </div>
                ))}
                {p.slide_count > 3 && <div className="pz-card-preview-more">+ {p.slide_count - 3} more</div>}
              </div>
              <button
                className="pz-play-card-btn"
                onClick={() => setPlaying(p)}
                data-testid={`pz-play-${p.id}`}
              >
                <Play size={16} fill="currentColor" /> Play presentation
              </button>
            </div>
          ))}
        </div>
      )}

      {builderOpen && (
        <BuilderModal
          existing={editing}
          onClose={() => { setBuilderOpen(false); setEditing(null); }}
          onSaved={onSaved}
        />
      )}

      {playing && (
        <PresentationPlayer presentation={playing} onClose={() => setPlaying(null)} />
      )}
    </div>
  );
}
